<template>
  <section :class="$style.day">
    <article
      v-if="formatToString(firstDate, true) !== formatDate(events[0].start, true)"
      :class="$style.event"
    >
      <h2 class="heading-3">
        <time
          :class="$style.date"
          :datetime="dateTime(firstDate)"
          v-html="formatToString(firstDate, true)"
        ></time>
      </h2>
      <p :class="$style.message">There are no events scheduled for this day.</p>
    </article>
    <article v-for="(event, key) in events" :key="key" :class="$style.event">
      <h2 v-if="isNewDate(key)" class="heading-3">
        <time
          :class="$style.date"
          :datetime="dateTime(event.start)"
          v-html="formatDate(event.start, true)"
        ></time>
      </h2>
      <router-link
        v-if="event.featuredImage"
        :class="$style.image"
        :to="{
          name: 'CalendarEvent',
          params: { date: event.start.isoDate, slug: event.eventSlug },
        }"
        :ga4-event="
          JSON.stringify({
            click_type: 'image',
            component: 'CalendarDay',
            content_group: 'calendar',
            gtm_tag: 'linked_image',
            file_name: `${event.featuredImage.node.sourceUrl.split('/').pop()}`,
            link_url: `${event.eventSlug}`,
          })
        "
      >
        <picture>
          <img
            :alt="
              event.specificImage ? event.specificImage.altText : event.featuredImage.node.altText
            "
            :class="[$style.img, 'aspect-image']"
            :src="
              event.specificImage
                ? event.specificImage.sourceUrl
                : event.featuredImage.node.sourceUrl
            "
            :srcset="event.specificImage ? event.specificImage.imageSrcset : event.imageSrcset"
            loading="lazy"
            sizes="(min-width: 960px) 470px, 100vw"
          />
        </picture>
      </router-link>
      <section :class="$style.text">
        <header>
          <router-link
            :class="$style.title"
            :to="{
              name: 'CalendarEvent',
              params: { date: event.start.isoDate, slug: event.eventSlug },
            }"
            :ga4-event="
              JSON.stringify({
                click_type: 'title',
                component: 'CalendarDay',
                content_group: 'calendar',
                gtm_tag: 'link',
                link_text: `${event.title}`,
                link_url: `${event.eventSlug}`,
              })
            "
          >
            <h3 class="heading-4" v-html="event.title"></h3>
          </router-link>
          <time
            v-if="!event.hideStartTime"
            class="subtitle-2"
            :datetime="dateTime(event.start)"
            v-html="`${formatTime(event.start)} ${event.timezoneAbbr}`"
          ></time>
          <p v-if="event.specificText && event.hideStartTime" v-html="event.specificText"></p>
        </header>
        <p v-html="event.excerpt"></p>
        <EventTypes
          v-if="Object.keys(event.eventTypes.nodes).length"
          :types="event.eventTypes.nodes"
        ></EventTypes>
      </section>
    </article>
  </section>
</template>

<script setup lang="ts">
import { defineAsyncComponent } from 'vue';
import { EventInterface } from '@/types/event.interface';
import { useDateFormat } from '@/composables/Common.js';

const EventTypes = defineAsyncComponent(() => import('@/components/calendar/EventTypes.vue'));

const props = defineProps<{
  events: EventInterface[];
  firstDate: Date;
}>();

const { dateTime, formatDate, formatTime, formatToString } = useDateFormat();

function isNewDate(key: number): boolean {
  return (
    key === 0 ||
    (key > 0 && props.events[key].start.isoDate !== props.events[key - 1].start.isoDate)
  );
}
</script>

<style lang="scss" module>
.date {
  @include space-48-64-above;
}

.day {
  margin-top: 4rem;
}

.event {
  @include space-48-64-above;

  &:first-of-type {
    @include space-28-32-above;
  }
}

.image {
  @include aspect-container;
  margin-top: 2rem;
}

.message {
  grid-column: span 12;
  margin-top: 2rem;
}

.text {
  margin-top: 1rem;

  ul + .title {
    h2 {
      margin-top: 0.25rem;
    }
  }
}

.title {
  margin-top: 0.5rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  h3 {
    margin-top: 0;
    width: fit-content;
  }
}

@media (min-width: $break-point) {
  .day {
    grid-column: span 12;
    margin-top: 2rem;
  }

  .event {
    @include grid;
    grid-column: span 12;

    h2 {
      grid-column: span 12;
    }
  }

  .image,
  .text {
    grid-column: span 6;
  }
  .image {
    margin-top: px-to-rem(37);
  }
  .text {
    margin-top: 2rem;
  }

  .title {
    margin-top: 2rem;
  }
}
</style>
